import { useCallback, useEffect, useRef, useState } from 'react';
import { Check, ClipboardPaste, Copy, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { copyToClipboard } from '@/lib/clipboard';
import {
  PROMO_SECTIONS,
  emptyPromoSections,
  formatPromoSections,
  mergePromoText,
  newPromoLine,
  parsePromoText,
  sanitizePriceInput,
  type PromoLine,
  type PromoSectionKey,
  type PromoSections,
} from '@/lib/promos';
import { MenuItemAutocomplete } from '@/components/events/menu-item-autocomplete';

const SECTION_MONO = 'font-mono text-[12px] uppercase tracking-wider text-muted-foreground';

interface Props {
  /** Texto formatado salvo em `event.promos`. */
  value: string | null;
  onChange: (value: string | null) => void;
}

function PriceInput({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="relative w-[96px] shrink-0">
      <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 font-mono text-[12px] text-muted-foreground">
        R$
      </span>
      <input
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(sanitizePriceInput(e.target.value))}
        placeholder="0,00"
        aria-label="Valor da promoção"
        className="w-full rounded-lg border border-border bg-card py-3 pl-8 pr-2 font-mono text-[12px] tabular-nums text-foreground outline-none transition-colors focus:border-foreground"
      />
    </div>
  );
}

export function PromoEditor({ value, onChange }: Props) {
  const [sections, setSections] = useState<PromoSections>(() =>
    value ? parsePromoText(value) : emptyPromoSections(),
  );
  const [copied, setCopied] = useState(false);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  // Última string emitida por nós — evita re-parsear (e perder o cursor) no eco do form.
  const emitted = useRef<string | null>(value);

  useEffect(() => {
    if (value === emitted.current) return;
    emitted.current = value;
    setSections(value ? parsePromoText(value) : emptyPromoSections());
  }, [value]);

  const commit = useCallback(
    (next: PromoSections) => {
      setSections(next);
      const text = formatPromoSections(next);
      emitted.current = text || null;
      onChange(text || null);
    },
    [onChange],
  );

  function patchLine(key: PromoSectionKey, index: number, patch: Partial<PromoLine>) {
    commit({
      ...sections,
      [key]: sections[key].map((l, i) => (i === index ? { ...l, ...patch } : l)),
    });
  }

  function addLine(key: PromoSectionKey) {
    commit({ ...sections, [key]: [...sections[key], newPromoLine()] });
  }

  function removeLine(key: PromoSectionKey, index: number) {
    const rest = sections[key].filter((_, i) => i !== index);
    commit({ ...sections, [key]: rest.length ? rest : [newPromoLine()] });
  }

  /** Distribui uma lista corrida nas categorias. Retorna false se nada foi reconhecido. */
  function distribuir(text: string): boolean {
    const { sections: next, adicionados } = mergePromoText(sections, text);
    if (adicionados === 0) {
      toast.error('Nenhum item reconhecido na lista');
      return false;
    }
    commit(next);
    toast.success(`${adicionados} ${adicionados === 1 ? 'item distribuído' : 'itens distribuídos'}`);
    setPasteOpen(false);
    setPasteText('');
    return true;
  }

  const preview = formatPromoSections(sections);

  async function handleCopy() {
    if (!preview) return;
    const ok = await copyToClipboard(preview);
    if (!ok) {
      toast.error('Não foi possível copiar');
      return;
    }
    setCopied(true);
    toast.success('Promoções copiadas no formato do card');
    window.setTimeout(() => setCopied(false), 1800);
  }

  return (
    <div className="w-full">
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className={SECTION_MONO}>Promoções</span>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => setPasteOpen((o) => !o)}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1.5 font-mono text-[11px] uppercase tracking-wider transition active:scale-[0.97]',
              pasteOpen ? 'border-foreground bg-muted text-foreground' : 'bg-card text-foreground hover:bg-muted',
            )}
          >
            <ClipboardPaste className="h-3.5 w-3.5" />
            Colar lista
          </button>
          <button
            type="button"
            onClick={handleCopy}
            disabled={!preview}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-2.5 py-1.5 font-mono text-[11px] uppercase tracking-wider transition active:scale-[0.97]',
              preview ? 'text-foreground hover:bg-muted' : 'cursor-not-allowed text-muted-foreground opacity-60',
            )}
          >
            {copied ? <Check className="h-3.5 w-3.5 text-success" /> : <Copy className="h-3.5 w-3.5" />}
            {copied ? 'Copiado' : 'Copiar'}
          </button>
        </div>
      </div>

      {pasteOpen && (
        <div className="mb-3 rounded-lg border border-border bg-surface-2/60 p-3">
          <textarea
            rows={6}
            autoFocus
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            onPaste={(e) => {
              // Colou a lista inteira de uma vez? Distribui na hora.
              const texto = e.clipboardData.getData('text');
              if (!texto.includes('\n')) return;
              e.preventDefault();
              distribuir(texto);
            }}
            placeholder={'BEATS ...................... R$ 11\nCAIPIRINHA LIMÃO DOBRADA ... R$ 18\nONION RINGS ................ R$ 25'}
            className="w-full resize-none whitespace-pre rounded-lg border border-border bg-card px-3 py-3 font-mono text-[12px] uppercase tabular-nums text-foreground outline-none transition-colors focus:border-foreground"
          />
          <div className="mt-2 flex items-center justify-between gap-2">
            <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
              Cada item vai pra sua categoria automaticamente
            </span>
            <button
              type="button"
              onClick={() => distribuir(pasteText)}
              disabled={!pasteText.trim()}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 font-mono text-[11px] font-semibold uppercase tracking-wider text-primary-foreground shadow-red transition active:scale-[0.97] disabled:opacity-50"
            >
              Distribuir
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-4">
        {PROMO_SECTIONS.map((def) => (
          <div key={def.key}>
            <p className={`${SECTION_MONO} mb-1.5 font-semibold text-foreground`}>{def.label}</p>
            <div className="flex flex-col gap-2">
              {sections[def.key].map((line, i) => {
                const isLast = i === sections[def.key].length - 1;
                return (
                  <div key={line.id} className="flex items-stretch gap-2">
                    <div className="min-w-0 flex-1">
                      <MenuItemAutocomplete
                        value={line.name}
                        onChange={(name) => patchLine(def.key, i, { name })}
                        kind={def.kind}
                        placeholder="Item do cardápio"
                        onPasteList={distribuir}
                        leading={
                          def.prefix ? (
                            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 font-mono text-[12px] font-semibold text-primary">
                              2X
                            </span>
                          ) : undefined
                        }
                      />
                    </div>
                    <PriceInput
                      value={line.price}
                      onChange={(price) => patchLine(def.key, i, { price })}
                    />
                    {isLast ? (
                      <button
                        type="button"
                        onClick={() => addLine(def.key)}
                        aria-label={`Adicionar item em ${def.label}`}
                        className="grid w-[46px] shrink-0 place-items-center rounded-lg border border-border bg-card text-primary transition active:scale-[0.97] hover:bg-muted"
                      >
                        <Plus className="h-4 w-4" strokeWidth={2.5} />
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => removeLine(def.key, i)}
                        aria-label={`Remover item de ${def.label}`}
                        className="grid w-[46px] shrink-0 place-items-center rounded-lg border border-border bg-card text-muted-foreground transition active:scale-[0.97] hover:text-destructive"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {preview ? (
        <pre className="mt-3 overflow-x-auto rounded-lg border border-border bg-surface-2 px-3 py-3 font-mono text-[11px] leading-relaxed text-muted-foreground">
          {preview}
        </pre>
      ) : (
        <p className={`${SECTION_MONO} mt-2`}>
          Digite 3 letras pra buscar o item — o texto sai pronto no botão copiar.
        </p>
      )}
    </div>
  );
}
